import { LOGIN_SUCCESS } from '@comeon/actions-public';
import reducerRegistry from '@comeon/reducer-registry';
import { requestApi } from '../../../services/api.js';
import { MARKETPLACE_REDEEMED_PROMOTION_ITEM } from '../../marketplace/marketplace-duck.js';
import imageFormatHandler from '../../utilities/image-format';
import { createApiAction } from '../../../services/middleware/api-middleware.js';
import { REMAINING_GERMANY_BONUS_VALUE_UPDATE } from '~/components/account/ducks';
import {
	FETCH_PROMOTIONS_AVAILABILITY_FAILURE,
	FETCH_PROMOTIONS_AVAILABILITY_REQUESTED,
	FETCH_PROMOTIONS_AVAILABILITY_SUCCESS,
	FETCH_PROMOTIONS_SUCCESS,
	FETCH_PROMOTIONS_FAILURE,
	FETCH_PROMOTIONS_REQUESTED,
	FETCH_THUNDERBITE_FAILURE,
	FETCH_THUNDERBITE_REQUESTED,
	FETCH_THUNDERBITE_SUCCESS,
	UPDATE_THUNDERBITE_TAB_OPEN,
	AVAILABLE_BONUS_FOR_USER_UPDATE,
	UPDATE_SELECTED_PROMOTIONS_SPLIT_CATEGORY,
	RESET_PROMOTIONS_AVAILABILITY_HYDRATED,
	REDUCER_NAME,
	END_POINT_AVAILABILITY,
	END_POINT_PROMOTIONS,
	END_POINT_PROMOTIONS_THUNDERBITE
} from './constants';
import {
	selectPromotionsIsAvailableForUserHydrated,
	selectIsPollingAvailability,
	selectThunderbiteIsPolling,
	selectPromotionsIsPolling
} from './selectors';
import { selectHasPromotions } from '~/store/feature-toggles/retention-feature-toggles';

// Action Creators
export const fetchPromotionsAvailability = () => {
	/// rest/state/promotions-availability endpoint
	return async (dispatch, getState) => {
		const hasPromotions = selectHasPromotions(getState());
		if (!hasPromotions) {
			return dispatch({ type: FETCH_PROMOTIONS_AVAILABILITY_FAILURE });
		}
		const hydrated = selectPromotionsIsAvailableForUserHydrated(getState());
		const isPolling = selectIsPollingAvailability(getState());
		if (!hydrated && !isPolling) {
			dispatch({ type: FETCH_PROMOTIONS_AVAILABILITY_REQUESTED });
			try {
				const response = await requestApi(END_POINT_AVAILABILITY);
				dispatch({
					type: FETCH_PROMOTIONS_AVAILABILITY_SUCCESS,
					payload: { isAvailableForUser: response.result }
				});
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error(error);
				dispatch({ type: FETCH_PROMOTIONS_AVAILABILITY_FAILURE });
			}
		}
	};
};

const fetchPromotionSuccessActionCreator =
	({ result: originalItems }) =>
	(dispatch, getState, { isPictureElementEnabled, imageDescriptors, imageFormats }) => {
		const promotionItems = isPictureElementEnabled
			? imageFormatHandler({ imageDescriptors, imageFormats })(originalItems)
			: originalItems;

		return dispatch({
			type: FETCH_PROMOTIONS_SUCCESS,
			payload: {
				promotionItems
			}
		});
	};

export const fetchPromotions = () => {
	return async (dispatch, getState) => {
		if (selectPromotionsIsPolling(getState())) return;
		dispatch({ type: FETCH_PROMOTIONS_REQUESTED });
		try {
			const response = await requestApi(END_POINT_PROMOTIONS);

			if (response.status === 'SUCCESS') {
				dispatch(fetchPromotionSuccessActionCreator(response));
			} else {
				dispatch({ type: FETCH_PROMOTIONS_AVAILABILITY_FAILURE });
			}
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error(error);
			dispatch({ type: FETCH_PROMOTIONS_AVAILABILITY_FAILURE });
		}
	};
};

const fetchThunderbiteSuccessActionCreator =
	({ result: originalItems }) =>
	(dispatch, getState, { isPictureElementEnabled, imageDescriptors, imageFormats }) => {
		const thunderbiteItems = isPictureElementEnabled
			? imageFormatHandler({ imageDescriptors, imageFormats })(originalItems)
			: originalItems;

		return dispatch({
			type: FETCH_THUNDERBITE_SUCCESS,
			payload: {
				thunderbiteItems
			}
		});
	};

export const fetchThunderbite = () => (dispatch, getState) => {
	if (selectThunderbiteIsPolling(getState())) return;
	return dispatch(
		createApiAction({
			endpoint: END_POINT_PROMOTIONS_THUNDERBITE,
			onStart: () => ({ type: FETCH_THUNDERBITE_REQUESTED }),
			onSuccess: fetchThunderbiteSuccessActionCreator,
			onError: () => ({ type: FETCH_THUNDERBITE_FAILURE })
		})
	);
};
export const updateThunderbiteTabOpen = payload => ({
	type: UPDATE_THUNDERBITE_TAB_OPEN,
	payload
});

export const availableBonusUpdate = payload => ({
	type: AVAILABLE_BONUS_FOR_USER_UPDATE,
	payload
});
export const updateSelectedPromotionsSplitCategory = payload => {
	return {
		type: UPDATE_SELECTED_PROMOTIONS_SPLIT_CATEGORY,
		payload
	};
};

// Reducer
const initialState = {
	isPolling: false,
	error: false,
	isHydrated: false,
	isAvailableForUser: false,
	isPollingAvailability: false,
	availabilityHydrated: false,
	selectedPromotionsSplitCategory: null,
	items: [],
	thunderbite: {
		isThunderbiteTabOpen: false,
		isPolling: false,
		error: false,
		isHydrated: false
	}
};

const reducer = (state = initialState, { type, payload }) => {
	switch (type) {
		case FETCH_PROMOTIONS_REQUESTED:
			return { ...state, isPolling: true };
		case FETCH_PROMOTIONS_SUCCESS:
			return {
				...state,
				isPolling: false,
				isHydrated: true,
				items: payload.promotionItems
			};
		case FETCH_PROMOTIONS_FAILURE:
			return { ...state, isPolling: false, error: true };
		case UPDATE_THUNDERBITE_TAB_OPEN: {
			return { ...state, thunderbite: { ...state.thunderbite, isThunderbiteTabOpen: payload } };
		}
		case FETCH_THUNDERBITE_REQUESTED:
			return {
				...state,
				thunderbite: {
					...state.thunderbite,
					isPolling: true
				}
			};
		case FETCH_THUNDERBITE_SUCCESS:
			return {
				...state,
				thunderbite: {
					...state.thunderbite,
					isPolling: false,
					isHydrated: true,
					items: payload.thunderbiteItems
				}
			};
		case FETCH_THUNDERBITE_FAILURE:
			return {
				...state,
				thunderbite: {
					...state.thunderbite,
					isPolling: false,
					error: true
				}
			};
		case FETCH_PROMOTIONS_AVAILABILITY_SUCCESS:
			return {
				...state,
				isAvailableForUser: payload.isAvailableForUser,
				isPollingAvailability: false,
				availabilityHydrated: true
			};
		case FETCH_PROMOTIONS_AVAILABILITY_REQUESTED:
			return { ...state, isPollingAvailability: true };
		case FETCH_PROMOTIONS_AVAILABILITY_FAILURE:
			return { ...state, availabilityHydrated: true };
		case MARKETPLACE_REDEEMED_PROMOTION_ITEM: {
			// Remove the item from the list of items if claim was successful
			return {
				...state,
				items: state.items.filter(item => item.id !== payload)
			};
		}
		case REMAINING_GERMANY_BONUS_VALUE_UPDATE: {
			return {
				...state,
				isHydrated: false
			};
		}
		case AVAILABLE_BONUS_FOR_USER_UPDATE:
			return {
				...state,
				isAvailableForUser: payload.hasAvailableBonuses,
				isHydrated: !payload.hasAvailableBonuses
			};
		case UPDATE_SELECTED_PROMOTIONS_SPLIT_CATEGORY: {
			return {
				...state,
				selectedPromotionsSplitCategory: payload
			};
		}
		case RESET_PROMOTIONS_AVAILABILITY_HYDRATED: {
			return {
				...state,
				availabilityHydrated: false,
				isHydrated: false
			};
		}
		case LOGIN_SUCCESS: {
			return {
				...state,
				isHydrated: false,
				thunderbite: {
					isHydrated: false
				}
			};
		}
		default:
			return state;
	}
};

reducerRegistry.register(REDUCER_NAME, reducer);

export default reducer;
